import moment from "moment";
import { chartColors } from "views/applications-id-report/slices/IncomeReportSlice/components/constants";
import {
  centsToAmount,
  mapTablePaystubData
} from "views/applications-id-report/utils";
import { VerifiedIncomeGraphData } from "../types";
import { ApplicantType, VerificationNames } from "shared/types";
import {
  BankIncome,
  SelfAttestedIncome,
  VerificationReport,
  VerifiedBankIncome,
  VerifiedIncomeFlowTypes,
  VerifiedIncomeReport,
  VerifiedPayrollIncome
} from "types/landlord/reports";
import api from "api/landlord/applications";
import { Applicant } from "types/application";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";

export const generateQueries = (
  applicants?: Applicant[],
  applicationId?: string
) => {
  return applicants?.flatMap((applicant) => {
    const verifiedIncomeVerification = applicant.verifications.find(
      (v) => v.name === VerificationNames.VerifiedIncomeVerification
    );
    const selfAttestedVerification = applicant.verifications.find(
      (v) => v.name === VerificationNames.IncomeVerification
    );
    const creditVerification = applicant.verifications.find(
      (v) => v.name === VerificationNames.CreditReport
    );

    const ret = verifiedIncomeVerification?.id
      ? [
          {
            queryKey: [
              "verifiedIncome",
              applicant.application_id,
              applicant.id,
              "_verifiedIncome_id_",
              verifiedIncomeVerification.id
            ],
            queryFn: () => {
              return api
                .getApplicantVerification(
                  Number(applicationId),
                  applicant.id,
                  verifiedIncomeVerification.id
                )
                .then((data) => {
                  return {
                    applicant: getFullName({
                      firstName: applicant.first_name,
                      lastName: applicant.last_name,
                      middleName: applicant.middle_name,
                      short: true
                    }),
                    role: applicant.role,
                    verification: data.verification
                  };
                });
            }
          }
        ]
      : [];

    if (selfAttestedVerification?.id)
      ret.push({
        queryKey: [
          "selfAttestedIncome",
          applicant.application_id,
          applicant.id,
          "_selfAttestedIncome_id_",
          selfAttestedVerification.id
        ],
        queryFn: () => {
          return api
            .getApplicantVerification(
              Number(applicationId),
              applicant.id,
              selfAttestedVerification.id
            )
            .then((data) => {
              return {
                applicant: getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                }),
                role: applicant.role,
                verification:
                  data.verification as VerificationReport<SelfAttestedIncome>
              };
            });
        }
      });

    if (creditVerification?.id)
      ret.push({
        queryKey: [
          "creditReport",
          applicant.application_id,
          applicant.id,
          "_creditReport_id_",
          creditVerification.id
        ],
        queryFn: () => {
          return api
            .getApplicantVerification(
              Number(applicationId),
              applicant.id,
              creditVerification.id
            )
            .then((data) => {
              return {
                applicant: getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                }),
                role: applicant.role,
                verification: data.verification
              };
            });
        }
      });

    return ret;
  });
};

// TODO: rewrite this ENTIRE function when release comes
export const getIncomeGraphsData = (
  verifiedIncomeData,
  selfAttestedIncomeData,
  monthlyRentAmount
) => {
  let colorIndex = 0;
  let totalPastThreeMonthsAvg: number | undefined = undefined;
  let totalVerifiedNet: number | undefined = undefined;

  const verifiedIncomeGraphData = verifiedIncomeData?.flatMap(
    (
      verifiedItem: {
        verification: VerificationReport<
          // God, please, no!
          VerifiedIncomeReport &
            VerifiedPayrollIncome &
            VerifiedBankIncome & {
              past_three_months_avg_total?: number;
            }
        >;
      } & {
        applicant: string;
        role: ApplicantType;
      }
    ) => {
      const color = chartColors[colorIndex % chartColors.length];
      colorIndex++;

      const role = getApplicantRole(verifiedItem.role);

      const verifiedNetIncomeCents =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        verifiedItem.verification?.report?.net_monthly_income?.cents;
      if (verifiedNetIncomeCents)
        totalVerifiedNet =
          totalVerifiedNet === undefined
            ? verifiedNetIncomeCents
            : totalVerifiedNet + verifiedNetIncomeCents;

      const isVerifiedDocumentIncome =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        verifiedItem.verification?.report?.income_source_type === "document";
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const bankIncome = verifiedItem.verification?.report?.bank_income || [];
      const isVerifiedPayrollIncome =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        verifiedItem.verification?.report?.income_source_type === "payroll" &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !verifiedItem.verification?.report?.flow_types?.includes(
          VerifiedIncomeFlowTypes.PAYROLL_DOCUMENT_INCOME
        );
      const isVerifiedPaystubIncome =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        verifiedItem.verification?.report?.income_source_type === "payroll" &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !verifiedItem.verification?.report?.flow_types?.includes(
          VerifiedIncomeFlowTypes.PAYROLL_DIGITAL_INCOME
        );

      const combinedData: VerifiedIncomeGraphData[] = [];

      if (isVerifiedDocumentIncome) {
        const documentIncome =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          verifiedItem.verification?.report?.net_income_summary
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            ?.reduce(
              (acc, entry) => {
                const formattedMonth = moment(entry.month, "YYYY-MM").format(
                  "MMM YY"
                );
                entry.sources.map((source) => {
                  let existingApplicant = acc.find(
                    (item) => item.id === verifiedItem.applicant
                  );

                  if (!existingApplicant) {
                    existingApplicant = {
                      id: verifiedItem.applicant,
                      role,
                      color,
                      data: []
                    };
                    acc.push(existingApplicant);
                  }

                  const existingMonth = existingApplicant.data.find(
                    (dataPoint) => dataPoint.x === formattedMonth
                  );

                  if (existingMonth)
                    existingMonth.y += (source.net_pay?.cents || 0) / 100;
                  else
                    existingApplicant.data.push({
                      x: formattedMonth,
                      y: (source.net_pay?.cents || 0) / 100
                    });
                });

                return acc;
              },
              [] as {
                id: string;
                color: string;
                role: string;
                data: { x: string; y: number }[];
              }[]
            )
            .map((applicant) => ({
              id: applicant.id,
              role: applicant.role,
              color: applicant.color,
              data: applicant.data.sort(
                (a, b) =>
                  moment(a.x, "MMM YY").valueOf() -
                  moment(b.x, "MMM YY").valueOf()
              )
            }));
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        documentIncome && combinedData.push(...documentIncome);
      }
      if (isVerifiedPayrollIncome || isVerifiedPaystubIncome) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const payrollData = verifiedItem.verification?.report?.items
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          ?.flatMap((item) => item.payroll_income)
          .map((income) => ({
            id: verifiedItem.applicant,
            role,
            color,
            data: mapTablePaystubData(income.pay_stubs)
              .reverse()
              .map((row: { date: string; total_net: number }) => ({
                x: moment(row.date, "MMM, YYYY").format("MMM YY"),
                y: centsToAmount(row.total_net)
              }))
          }));
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        payrollData && combinedData.push(...payrollData);
      }

      if (bankIncome.length > 0) {
        const combinedHistoricalSummary = bankIncome.reduce(
          (acc: BankIncome["historical_summary"], current) => {
            return acc.concat(current.historical_summary);
          },
          []
        );

        const bankData = {
          id: verifiedItem.applicant,
          role,
          color,
          data: combinedHistoricalSummary.map((hs) => ({
            x: moment(hs.start_date).format("MMM YY"),
            y: centsToAmount(hs.total_amount?.cents || 0)
          }))
        };

        combinedData.push(bankData);
      }
      // verifiedItem.verification?.report?.past_three_months_avg_total || --< was like this before. Saved for future reference
      const pastThreeMonthsAvg =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        verifiedItem.verification?.report?.gross_monthly_income?.cents ===
        undefined
          ? undefined
          : verifiedItem.verification.report.gross_monthly_income.cents / 100;

      if (pastThreeMonthsAvg)
        totalPastThreeMonthsAvg =
          totalPastThreeMonthsAvg === undefined
            ? pastThreeMonthsAvg
            : (totalPastThreeMonthsAvg += pastThreeMonthsAvg);

      return combinedData;
    }
  );
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const pastThreeMonthsAvgTotal = totalPastThreeMonthsAvg || undefined;

  const netIncomeToRentRatio =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    monthlyRentAmount && pastThreeMonthsAvgTotal !== undefined
      ? pastThreeMonthsAvgTotal / monthlyRentAmount
      : undefined;

  const totalSelfAttestedCurrentIncome = selfAttestedIncomeData?.reduce(
    (total: number | undefined, selfAttestedItem) => {
      const currentIncome =
        selfAttestedItem?.verification?.report?.current_income?.cents;

      if (!currentIncome) return total;
      return total === undefined ? currentIncome : total + currentIncome;
    },
    undefined
  );

  const uniqueDebts = verifiedIncomeData
    ?.flatMap((item) => item?.verification?.report?.debts || [])
    .reduce((acc, debt) => {
      if (debt.name === "listed_rent_amount") {
        // Add the first "listed_rent_amount" found, ignore subsequent ones
        if (!acc.some((d) => d.name === "listed_rent_amount")) acc.push(debt);
      } else acc.push(debt);
      return acc;
    }, []);

  const totalDebtsAmount = uniqueDebts.reduce(
    (total, debt) => total + (debt.amount?.cents || 0),
    0
  );

  const selfAttestedIncomeToRentRatio =
    monthlyRentAmount && totalSelfAttestedCurrentIncome !== undefined
      ? totalSelfAttestedCurrentIncome / monthlyRentAmount / 100
      : undefined;

  return {
    verifiedIncomeGraphData,
    pastThreeMonthsAvgTotal,
    selfAttestedIncomeToRentRatio,
    netIncomeToRentRatio,
    totalSelfAttestedCurrentIncome,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    totalDebtToNetIncome: totalVerifiedNet
      ? (totalDebtsAmount / 100 / (totalVerifiedNet / 100)) * 100
      : undefined,
    totalVerifiedNet:
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      totalVerifiedNet === undefined ? undefined : totalVerifiedNet / 100
  };
};
